"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

interface LazyVideoProps {
  src: string;
  poster?: string;
  className?: string;
  controls?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  preload?: "none" | "metadata" | "auto";
  "aria-label"?: string;
  /**
   * If provided, the component will generate a poster image from the given timestamp (in seconds)
   * using a canvas snapshot. This enables using a specific frame (e.g., 11s) as the poster.
   * When set, the video will preload metadata to allow seeking before interaction.
   */
  posterTimestampSec?: number;
}

// Helper function to infer MIME type from file extension
function getMimeTypeFromExtension(src: string): string {
  const extension = src.toLowerCase().split(".").pop();
  switch (extension) {
    case "mp4":
      return "video/mp4";
    case "mov":
      return "video/quicktime";
    case "webm":
      return "video/webm";
    case "ogg":
      return "video/ogg";
    case "avi":
      return "video/x-msvideo";
    case "mkv":
      return "video/x-matroska";
    default:
      return "video/mp4"; // Default fallback
  }
}

export function LazyVideo({
  src,
  poster,
  className = "",
  controls = true,
  autoplay = false,
  loop = false,
  muted = true, // Required for autoplay in most browsers
  playsInline = true,
  preload = "none",
  "aria-label": ariaLabel,
  posterTimestampSec,
}: LazyVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [generatedPosterUrl, setGeneratedPosterUrl] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Create Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasLoaded) {
          setIsIntersecting(true);
          setHasLoaded(true);
          // Disconnect observer after first intersection
          observer.disconnect();
        }
      },
      {
        // Load when video is 10% visible
        threshold: 0.1,
        // Start loading 100px before entering viewport
        rootMargin: "100px",
      },
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [hasLoaded]);

  // Generate a poster image from a specific timestamp, if requested
  useEffect(() => {
    if (posterTimestampSec === undefined) return;
    const video = videoRef.current;
    if (!video) return;

    let revokedUrl: string | undefined;
    let seekedHandler: (() => void) | null = null;
    let loadedMetadataHandler: (() => void) | null = null;

    const captureFrame = () => {
      try {
        const width = video.videoWidth || 0;
        const height = video.videoHeight || 0;
        if (width === 0 || height === 0) return;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return;
            // Cleanup any previous URL
            if (revokedUrl) URL.revokeObjectURL(revokedUrl);
            const url = URL.createObjectURL(blob);
            setGeneratedPosterUrl(url);
            revokedUrl = url;
          },
          "image/jpeg",
          0.9,
        );
      } catch {
        // Silently ignore capture errors to avoid breaking UX
      }
    };

    loadedMetadataHandler = () => {
      const duration =
        Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : posterTimestampSec + 0.1;
      const safeTarget = Math.max(
        0,
        Math.min(posterTimestampSec, duration - 0.1),
      );
      // After we seek, capture the frame once
      seekedHandler = () => {
        // Tiny delay helps Safari after a seek
        setTimeout(captureFrame, 0);
        if (seekedHandler) video.removeEventListener("seeked", seekedHandler);
      };
      video.addEventListener("seeked", seekedHandler);
      try {
        video.currentTime = safeTarget;
      } catch {
        // If setting currentTime fails (rare), skip capture
      }
    };

    video.addEventListener("loadedmetadata", loadedMetadataHandler);

    return () => {
      if (loadedMetadataHandler)
        video.removeEventListener("loadedmetadata", loadedMetadataHandler);
      if (seekedHandler) video.removeEventListener("seeked", seekedHandler);
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [posterTimestampSec]);

  // Handle video load events for performance monitoring
  const handleLoadStart = () => {
    // Video started loading
  };

  const handleCanPlay = () => {
    // Video can start playing
  };

  const handleLoadedData = () => {
    // Video data has loaded
  };

  const effectivePoster = generatedPosterUrl ?? poster;
  const effectivePreload =
    posterTimestampSec !== undefined ? "metadata" : preload;

  return (
    <div className={`relative w-full aspect-video ${className}`}>
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        controls={controls}
        autoPlay={autoplay && isIntersecting}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        preload={effectivePreload}
        poster={effectivePoster}
        crossOrigin={posterTimestampSec !== undefined ? "anonymous" : undefined}
        aria-label={ariaLabel}
        onLoadStart={handleLoadStart}
        onCanPlay={handleCanPlay}
        onLoadedData={handleLoadedData}
      >
        {/* Only render source when component is in viewport */}
        {(isIntersecting || posterTimestampSec !== undefined) && (
          <source src={src} type={getMimeTypeFromExtension(src)} />
        )}
        {/* Fallback for browsers that don't support video */}
        <p className="relative flex items-center justify-center h-full bg-gray-100 text-gray-600">
          Your browser doesn't support video playback.
          {effectivePoster && (
            <Image
              src={effectivePoster}
              alt="Video thumbnail"
              fill
              className="object-cover"
            />
          )}
        </p>
      </video>

      {/* Loading placeholder when not yet intersecting */}
      {!isIntersecting && effectivePoster && (
        <div className="absolute inset-0 bg-gray-100">
          <Image
            src={effectivePoster}
            alt="Video thumbnail"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 60vw"
            className="object-cover"
            priority={false}
          />
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
            <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-gray-800 ml-1"
                fill="currentColor"
                viewBox="0 0 24 24"
                role="img"
                aria-labelledby="play-icon-title"
              >
                <title id="play-icon-title">Play video</title>
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
